import React, { useState, useEffect, useRef } from "react";
import PulseLoader from "react-spinners/PulseLoader";

import { ContentBlock } from "../blocks";

import { wp } from "../providers/content";

const validateEmail = (email) => {
  const mailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return mailRegex.test(String(email).toLowerCase());
};

const Contact = () => {
  const [state, setState] = useState({ name: "", email: "", message: "", dsgvo: "", success: "", error: "", loading: "" });
  const { name, email, message, dsgvo, success, error, loading } = state;

  // Spamschutz (util/form-guard.php): signiertes Token mit Zeitstempel + Köderfeld "website".
  // Der Server verwirft Token, die jünger als 3 s sind; ein frisch geholtes Token wird daher kurz abgewartet.
  const formToken = useRef({ token: "", fetchedAt: 0 });
  const website = useRef(null);

  const loadFormToken = () =>
    wp
      .formToken()
      .get()
      .then((response) => {
        formToken.current = { token: response.token, fetchedAt: Date.now() };
      })
      .catch(() => {});

  const readyFormToken = () =>
    (formToken.current.token ? Promise.resolve() : loadFormToken()).then(
      () =>
        new Promise((resolve) =>
          setTimeout(resolve, Math.max(0, 3500 - (Date.now() - formToken.current.fetchedAt)))
        )
    );

  useEffect(() => {
    loadFormToken();
  }, []);

  const onChange = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.name === "dsgvo" ? e.target.checked : e.target.value,
      success: undefined,
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();

    let errorList = [];

    if (!name) {
      errorList.push("Bitte geben Sie einen Namen an.");
    } else if (name.trim().length < 4) {
      errorList.push("Bitte geben Sie einen hinreichend langen Namen an.");
    }

    if (!email) {
      errorList.push("Bitte geben Sie Ihre E-Mail Addresse an.");
    } else if (!validateEmail(email)) {
      errorList.push("Bitte geben Sie eine valide E-Mail Addresse an.");
    }

    if (!message) {
      errorList.push("Bitte geben Sie eine Nachricht an.");
    }

    if (!dsgvo) {
      errorList.push("Bitte bestätigen Sie, dass Sie die Datenschutzerklärung zur Kenntnis genommen haben.");
    }

    const resetState = {
      ...state,
      name: "",
      email: "",
      message: "",
      dsgvo: false,
      loading: undefined,
    };

    if (errorList.length === 0) {
      setState({
        ...state,
        error: [],
        loading: true,
      });
      readyFormToken()
        .then(() =>
          wp.contact().create({
            name,
            email,
            message,
            dsgvo: !!dsgvo,
            form_token: formToken.current.token,
            website: website.current ? website.current.value : "",
          })
        )
        .then((response) => {
          // Nach jeder Antwort ein neues Token für die nächste Nachricht holen.
          formToken.current = { token: "", fetchedAt: 0 };
          loadFormToken();
          if (response.status === 200) {
            setState({
              ...resetState,
              success: [response.message],
            });
          } else {
            setState({
              ...state,
              error: [response.message],
            });
          }
        })
        .catch((error) => {
          setState({
            ...state,
            error: ["Es gab leider ein Fehler beim Versenden Ihrer Nachricht, bitte versuchen Sie es erneut."],
          });
        });
    } else {
      setState({ ...state, error: errorList, success: undefined });
    }
  };

  return (
    <>
      <ContentBlock>
        <h1 className="title mebm-page-title">Kontaktieren Sie uns</h1>
        <div className="well well-sm">
          <form className="form-horizontal pt-2" action="" method="post" onSubmit={onSubmit}>
            <fieldset>
              {error && error.length > 0 && (
                <div className="alert alert-danger rounded-0" role="alert">
                  {error.map((msg, i) => (
                    <p className="mb-0" key={i}>
                      {msg}
                    </p>
                  ))}
                </div>
              )}
              {success && success.length > 0 && (
                <div className="alert alert-success rounded-0" role="alert">
                  {success.map((msg, i) => (
                    <p className="mb-0" key={i}>
                      {msg}
                    </p>
                  ))}
                </div>
              )}
              {loading && (
                <div className="d-flex justify-content-center py-5">
                  <PulseLoader />
                </div>
              )}
              {!loading && (
                <>
                  <div className="form-group">
                    <label className="control-label" htmlFor="name">
                      Name:
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Name"
                      className="form-control rounded-0"
                      onChange={onChange}
                      value={name}
                    />
                  </div>
                  <div className="form-group">
                    <label className="control-label" htmlFor="email">
                      E-Mail:
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="text"
                      placeholder="E-Mail"
                      className="form-control rounded-0"
                      onChange={onChange}
                      value={email}
                    />
                  </div>
                  <div className="form-group">
                    <label className="control-label" htmlFor="message">
                      Ihre Nachricht:
                    </label>
                    <textarea
                      className="form-control rounded-0"
                      id="message"
                      name="message"
                      placeholder="Bitte tragen Sie hier Ihre Nachricht ein."
                      rows="5"
                      onChange={onChange}
                      value={message}
                    />
                  </div>
                  <div className="form-check">
                    <input
                      name="dsgvo"
                      type="checkbox"
                      className="form-check-input"
                      id="dsgvo"
                      defaultChecked={dsgvo}
                      onChange={onChange}
                    />
                    <label className="form-check-label" htmlFor="dsgvo">
                      Bitte bestätigen Sie, dass Sie unsere <a href="/datenschutz">Datenschutzerklärung</a> zur Kenntnis genommen haben.
                    </label>
                  </div>
                  <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
                    <label htmlFor="website">Website (bitte leer lassen)</label>
                    <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" ref={website} defaultValue="" />
                  </div>
                  <div className="form-group pt-3">
                    <div className="text-right">
                      <button type="submit" className="btn btn-info btn-sm rounded-0">
                        Abschicken
                      </button>
                    </div>
                  </div>
                </>
              )}
            </fieldset>
          </form>
        </div>
      </ContentBlock>
    </>
  );
};

export default Contact;
